import type { CoachmarkTargetRect } from "@/hooks/useCoachmarkTargetRect";

export type CoachmarkPlacement = "above" | "below";

export interface CoachmarkLayout {
  arrowLeft: number;
  calloutLeft: number;
  calloutMaxHeight: number;
  calloutTop: number;
  calloutWidth: number;
  placement: CoachmarkPlacement;
}

const CALLOUT_GAP = 16;
const MAX_CALLOUT_WIDTH = 352;
const VIEWPORT_GUTTER = 16;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

export function getCoachmarkLayout(
  target: CoachmarkTargetRect,
  calloutHeight: number,
): CoachmarkLayout {
  const availableWidth = Math.max(0, target.viewportWidth - VIEWPORT_GUTTER * 2);
  const calloutWidth = Math.min(MAX_CALLOUT_WIDTH, availableWidth);
  const maximumLeft = Math.max(
    VIEWPORT_GUTTER,
    target.viewportWidth - calloutWidth - VIEWPORT_GUTTER,
  );
  const calloutLeft = clamp(target.left, VIEWPORT_GUTTER, maximumLeft);
  const spaceBelow = Math.max(
    0,
    target.viewportHeight - target.bottom - CALLOUT_GAP - VIEWPORT_GUTTER,
  );
  const spaceAbove = Math.max(
    0,
    target.top - CALLOUT_GAP - VIEWPORT_GUTTER,
  );
  const placement: CoachmarkPlacement =
    spaceBelow >= Math.min(calloutHeight, 240) || spaceBelow >= spaceAbove
      ? "below"
      : "above";
  const calloutMaxHeight = placement === "below" ? spaceBelow : spaceAbove;
  const visibleCalloutHeight = Math.min(calloutHeight, calloutMaxHeight);
  const calloutTop =
    placement === "below"
      ? target.bottom + CALLOUT_GAP
      : target.top - CALLOUT_GAP - visibleCalloutHeight;
  const targetAnchor = target.left + Math.min(48, target.width / 2);
  const arrowLeft = clamp(
    targetAnchor - calloutLeft - 10,
    20,
    Math.max(20, calloutWidth - 40),
  );

  return {
    arrowLeft,
    calloutLeft,
    calloutMaxHeight,
    calloutTop,
    calloutWidth,
    placement,
  };
}
